import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { trackNavigation } from '../utils/analytics';
import SEO from '../components/SEO';

const MediaContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const MediaHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  margin: 0 0 3rem 0;

  @media (max-width: 767px) {
    margin: 0 -2rem 3rem -2rem;
    border-radius: 0;
  }
`;

const MediaTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const MediaSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.95;
  font-style: italic;
  line-height: 1.5;
  max-width: 800px;
  margin: 0 auto;
`;

const Section = styled(motion.section)`
  margin-bottom: 3rem;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
  text-align: center;
`;

const SectionDescription = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.gray};
`;

const ArticleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 2rem 0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ArticleCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  text-align: left;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
    border-color: #3182ce;
  }
`;

const PublicationHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const PublicationInfo = styled.div`
  flex: 1;
`;

const PublicationName = styled.h3`
  color: #2d3748;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

const PublicationType = styled.span`
  color: #4a5568;
  font-size: 0.9rem;
  font-style: italic;
`;

const PublicationDate = styled.span`
  color: #718096;
  font-size: 0.85rem;
  font-weight: 500;
  background: #f7fafc;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  margin-left: auto;
`;

const ArticleTitle = styled.h4`
  color: #2c5282;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const ArticleDescription = styled.p`
  color: #4a5568;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ReadArticleButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(44, 82, 130, 0.3);
    text-decoration: none;
    color: white;
  }

  &:after {
    content: '↗';
    font-size: 1rem;
  }
`;

const ImpactSection = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  margin-top: 2rem;
  border: 1px solid rgba(0,0,0,0.05);
`;

const ImpactTitle = styled.h3`
  color: #2c3e50;
  font-size: 1.3rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ImpactDescription = styled.p`
  color: #4a5568;
  font-size: 1rem;
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto;
`;

const NextSectionContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin: 3rem auto 2rem auto;
  text-align: center;
  max-width: 600px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
`;

const NextSectionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
`;

const MediaRecognition = () => {
  // News articles data
  const newsArticles = [
    {
      publication: "Herald Extra",
      type: "Local Saratoga Springs Coverage",
      date: "July 31, 2025",
      title: "Miraculous recovery: Saratoga Springs family recounts mother's medical emergency, daughter praised for life-saving efforts",
      description: "Local newspaper coverage highlighting the miraculous survival story and recognizing Eliza's heroic CPR efforts that helped save her mother's life during the Thanksgiving Day emergency.",
      url: "https://www.heraldextra.com/news/2025/jul/31/miraculous-recovery-saratoga-springs-family-recounts-mothers-medical-emergency-daughter-praised-for-life-saving-efforts/",
      trackingLabel: "Herald Extra Article"
    },
    {
      publication: "EMS World",
      type: "Professional Medical Publication",
      date: "Published 2025",
      title: "Daughter's Bystander CPR Saves Mother Thanksgiving Day",
      description: "Professional medical industry publication featuring the story as an example of successful bystander CPR intervention and the importance of CPR training in saving lives.",
      url: "https://www.hmpgloballearningnetwork.com/site/emsworld/feature/daughters-bystander-cpr-saves-mother-thanksgiving-day?fbclid=IwY2xjawOT9dNleHRuA2FlbQIxMQBzcnRjBmFwcF9pZBAyMjIwMzkxNzg4MjAwODkyAAEenhcvh2LvPWJkITQ3_5HfsIqWk196QdZpNQW-P4qXKJ4aYR05SlMbrE5H4VM_aem_wvBXK51KQqQihLQSnLUJ-w",
      trackingLabel: "EMS World Article"
    },
    {
      publication: "The Mirror",
      type: "International Coverage",
      date: "Published 2025",
      title: "Hero daughter saves mom Thanksgiving",
      description: "International media coverage sharing the inspiring story of a daughter's quick thinking and CPR training that saved her mother's life, reaching audiences worldwide.",
      url: "https://www.themirror.com/news/us-news/hero-daughter-saves-mom-thanksgiving-1528548",
      trackingLabel: "The Mirror Article"
    }
  ];

  const handleArticleClick = (label) => {
    trackNavigation(`Media Recognition - ${label}`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  return (
    <MediaContainer>
      <SEO
        title="Media Recognition - Professional Coverage of Maggie's Miracle"
        description="Professional media coverage and recognition of Maggie's miraculous survival story from Herald Extra, EMS World, and The Mirror. External validation of the life-saving emergency response and community support."
        url="/media-recognition"
        image="/uploads/EMTS_AT_WORK.jpg"
        type="website"
      />

      <MediaHeader>
        <MediaTitle>Media Recognition</MediaTitle>
        <MediaSubtitle>
          Professional media coverage highlighting the miraculous story and recognizing the heroic efforts that saved Maggie's life
        </MediaSubtitle>
      </MediaHeader>

      <Section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <SectionTitle>Professional Media Coverage</SectionTitle>
        <SectionDescription>
          Maggie's miraculous story has gained recognition from reputable news sources,
          from local Saratoga Springs coverage to professional medical publications and international media.
          These articles validate the extraordinary nature of her survival and the heroic response that made it possible.
        </SectionDescription>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <ArticleGrid>
            {newsArticles.map((article, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <ArticleCard>
                  <PublicationHeader>
                    <PublicationInfo>
                      <PublicationName>{article.publication}</PublicationName>
                      <PublicationType>{article.type}</PublicationType>
                    </PublicationInfo>
                    <PublicationDate>{article.date}</PublicationDate>
                  </PublicationHeader>

                  <ArticleTitle>{article.title}</ArticleTitle>
                  <ArticleDescription>{article.description}</ArticleDescription>

                  <ReadArticleButton
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleArticleClick(article.trackingLabel)}
                  >
                    Read Full Article
                  </ReadArticleButton>
                </ArticleCard>
              </motion.div>
            ))}
          </ArticleGrid>
        </motion.div>

        <ImpactSection>
          <ImpactTitle>External Validation & Recognition</ImpactTitle>
          <ImpactDescription>
            The professional media coverage of Maggie's story serves as external validation of the miraculous nature of her survival
            and the exceptional response by emergency personnel and family. From local recognition to professional medical publications
            and international coverage, these articles highlight the extraordinary coordination of events that made this miracle possible.
          </ImpactDescription>
        </ImpactSection>
      </Section>

      <NextSectionContainer>
        <NextSectionButton href="/elizas-award">
          Heroes Recognition →
        </NextSectionButton>
      </NextSectionContainer>
    </MediaContainer>
  );
};

export default MediaRecognition;